/**
 * **Black Thumb Rodeo** (`t_black_thumb:2`, Long Rim `@massif/long-rim-data` 1.2.2): "As a
 * protocol, you give up the controls, pop the hatch on your mech and partly climb out, starting a
 * BLACK THUMB RODEO and gaining a personal shield (as above). While in rodeo, your pilot is immune
 * to involuntary movement, occupies your mech's space and moves when it moves. This does not count
 * as DISMOUNT [...] If you lose your personal shield for any reason, you are immediately forced
 * back inside your mech, ending the effect, and you cannot start a rodeo again on your following
 * turn. You can also end a rodeo as a quick action on any of your turns."
 *
 * The step builders behind the `begin_rodeo` / `end_rodeo` atoms. The rider is a pilot combatant
 * minted from the mech's frozen `pilotBlock` (`spawnDismountedPilot`, the Dismount mint) on the
 * mech's own cell, through `spawn_object`: it joins `combatants` but not `order`, so it is a
 * target and never takes a turn of its own. The mech keeps acting; it is never Unpiloted, since
 * this is not a Dismount. The pair is the third co-location pair (`RODEO_STATUS` on the mech,
 * `RODEO_RIDER_STATUS` on the rider, each `partner`-stamped at the other), so it shares Jockey's
 * occupancy exemption and `jockeyRecomputer`'s drag. Ending it despawns the rider.
 */

import type { CombatantId, EncounterState, ResolvedStep, StatusExpiry } from "@lancer-vtt/domain";
import { RODEO_RIDER_STATUS, RODEO_STATUS, UNPILOTED_STATUS, hasStatus } from "@lancer-vtt/domain";
import { ok, err, type Result } from "@lancer-vtt/shared";
import { dismountedPilotIdFor, spawnDismountedPilot } from "./pilotBridge";
import { destroyedConversionKind } from "./wreckConversion";

/** A status the rodeo plants on its rider or its mech, beside the markers (the personal shield, the lockout). */
export type RodeoStatus = { status: string; until?: StatusExpiry };

/** May `mechId` start a rodeo? A placed, piloted `spawnMech` mech, not already in one, whose pilot isn't already on the map. */
export function checkRodeoLegality(state: EncounterState, mechId: CombatantId): Result<void> {
  const mech = state.combatants[mechId];
  if (!mech) return err(`rodeo: no such combatant ${mechId}`);
  if (!mech.base.pilotBlock) return err(`rodeo: ${mech.name} carries no pilot`);
  if (hasStatus(mech.statuses, UNPILOTED_STATUS)) return err(`rodeo: ${mech.name} is Unpiloted`);
  if (hasStatus(mech.statuses, RODEO_STATUS)) return err(`rodeo: ${mech.name} is already in a rodeo`);
  if (!mech.pos) return err(`rodeo: ${mech.name} isn't placed on the map`);
  if (state.combatants[dismountedPilotIdFor(mechId)]) return err(`rodeo: ${mech.name}'s pilot is already on the map`);
  return ok(undefined);
}

/** A status-add step for `status` on `target`, `source`-stamped at `source`; a turn expiry gets this turn's `appliedTurn`. */
function rodeoStatusAdd(state: EncounterState, target: CombatantId, add: RodeoStatus & { source: CombatantId; partner?: CombatantId }): ResolvedStep {
  const expiry = add.until ?? { kind: "until_removed" };
  return {
    op: "status_add",
    target,
    status: add.status,
    expiry,
    source: add.source,
    ...(add.partner !== undefined ? { partner: add.partner } : {}),
    ...(expiry.kind === "turn" ? { appliedTurn: state.activation.turnOrdinal } : {}),
  };
}

/**
 * The steps that start `mechId`'s rodeo: mint the rider on the mech's cell, pair the markers, then
 * plant `riderStatuses` on the rider (the personal shield). Assumes {@link checkRodeoLegality}.
 */
export function beginRodeoSteps(state: EncounterState, mechId: CombatantId, riderStatuses: readonly RodeoStatus[] = []): ResolvedStep[] {
  const mech = state.combatants[mechId];
  if (!mech?.pos) return [];
  const riderId = dismountedPilotIdFor(mechId);
  const rider = spawnDismountedPilot(mech, riderId, mech.pos);
  return [
    { op: "spawn_object", combatant: rider },
    rodeoStatusAdd(state, riderId, { status: RODEO_RIDER_STATUS, source: mechId, partner: mechId }),
    rodeoStatusAdd(state, mechId, { status: RODEO_STATUS, source: mechId, partner: riderId }),
    ...riderStatuses.map((s) => rodeoStatusAdd(state, riderId, { ...s, source: mechId })),
  ];
}

/** The mech and rider of the rodeo `selfId` is part of, from either side's marker; `undefined` when it is in none. */
export function rodeoPair(state: EncounterState, selfId: CombatantId): { mechId: CombatantId; riderId: CombatantId } | undefined {
  const self = state.combatants[selfId];
  const carrier = self?.statuses.find((s) => s.name === RODEO_STATUS);
  if (carrier?.partner !== undefined) return { mechId: selfId, riderId: carrier.partner };
  const rider = self?.statuses.find((s) => s.name === RODEO_RIDER_STATUS);
  if (rider?.partner !== undefined) return { mechId: rider.partner, riderId: selfId };
  return undefined;
}

/**
 * The steps that end the rodeo `selfId` (mech or rider) is part of: clear the mech's marker,
 * despawn the rider (its own marker and shield go with it), and plant `lockout` on the mech when
 * the end was forced. Empty when `selfId` is in no rodeo.
 */
export function endRodeoSteps(state: EncounterState, selfId: CombatantId, lockout?: RodeoStatus): ResolvedStep[] {
  const pair = rodeoPair(state, selfId);
  if (!pair) return [];
  const steps: ResolvedStep[] = [];
  const mark = state.combatants[pair.mechId]?.statuses.find((s) => s.name === RODEO_STATUS);
  if (mark) {
    steps.push({
      op: "status_remove",
      target: pair.mechId,
      status: RODEO_STATUS,
      ...(mark.expiry !== undefined ? { expiry: mark.expiry } : {}),
      ...(mark.appliedTurn !== undefined ? { appliedTurn: mark.appliedTurn } : {}),
      ...(mark.source !== undefined ? { source: mark.source } : {}),
      ...(mark.partner !== undefined ? { partner: mark.partner } : {}),
    });
  }
  const rider = state.combatants[pair.riderId];
  if (rider) steps.push({ op: "despawn_object", combatant: rider });
  if (lockout && state.combatants[pair.mechId]) steps.push(rodeoStatusAdd(state, pair.mechId, { ...lockout, source: pair.mechId }));
  return steps;
}

/**
 * The rodeos a destruction ends: every placed, destroyed mech still carrying `RODEO_STATUS`. The
 * rider is despawned with the marker, so `resolvePilotEjections` (which skips a mech whose pilot id
 * is live) then ejects the pilot the ordinary way beside the wreck, as a unit with turns of its own.
 * Run before the ejection sweep, while the dead mech still has its `pos`.
 */
export function rodeoEndsAtDestruction(state: EncounterState): ResolvedStep[] {
  return Object.values(state.combatants)
    .filter((c) => destroyedConversionKind(c) !== null && hasStatus(c.statuses, RODEO_STATUS))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .flatMap((c) => endRodeoSteps(state, c.id));
}
